// src/store/api/townshipApi.ts
import {baseApi} from "./baseApi";
import type {
  CreateTownshipDto,
  UpdateTownshipDto,
  TownshipFilterDto,
  TownshipResponseDto,
  TownshipListResponseDto,
} from "../../types/township";

interface TownshipStatsDto {
  totalTownships: number;
  townshipsPerRegion: Array<{
    regionId: number;
    regionName: string;
    townshipCount: number;
  }>;
  averageTownshipsPerRegion: number;
  regionsWithoutTownships: number;
  mostPopularTownship: string;
  mostPopularTownshipCount: number;
}

export const townshipApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create Township
    createTownship: builder.mutation<TownshipResponseDto, CreateTownshipDto>({
      query: (data) => ({
        url: "/townships",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Township"],
    }),

    // Get Townships with filters and pagination
    getTownships: builder.query<TownshipListResponseDto, TownshipFilterDto>({
      query: (params) => ({
        url: "/townships",
        params,
      }),
      transformResponse: (response: {data: TownshipListResponseDto}) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({id}) => ({type: "Township" as const, id})),
              {type: "Township", id: "LIST"},
            ]
          : [{type: "Township", id: "LIST"}],
    }),

    // Get Township Statistics
    getTownshipStatistics: builder.query<TownshipStatsDto, void>({
      query: () => "/townships/statistics",
      transformResponse: (response: {data: TownshipStatsDto}) => response.data,
      providesTags: ["Township"],
    }),

    // Get Townships by Region
    getTownshipsByRegion: builder.query<TownshipResponseDto[], number>({
      query: (regionId) => `/townships/region/${regionId}`,
      transformResponse: (response: {data: TownshipResponseDto[]}) => response.data,
      providesTags: (result, error, regionId) => [
        {type: "Township", id: `REGION_${regionId}`},
      ],
    }),

    // Get Township by Slug
    getTownshipBySlug: builder.query<TownshipResponseDto, string>({
      query: (slug) => `/townships/slug/${slug}`,
      transformResponse: (response: {data: TownshipResponseDto}) => response.data,
      providesTags: (result) =>
        result ? [{type: "Township", id: result.id}] : [],
    }),

    // Get Township by ID
    getTownshipById: builder.query<TownshipResponseDto, number>({
      query: (id) => `/townships/${id}`,
      transformResponse: (response: {data: TownshipResponseDto}) => response.data,
      providesTags: (result, error, id) => [{type: "Township", id}],
    }),

    // Update Township
    updateTownship: builder.mutation<
      TownshipResponseDto,
      {id: number; data: UpdateTownshipDto}
    >({
      query: ({id, data}) => ({
        url: `/townships/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "Township", id},
        {type: "Township", id: "LIST"},
        {type: "Township", id: `REGION_${result?.regionId}`},
      ],
    }),

    // Delete Township
    deleteTownship: builder.mutation<
      {success: boolean; message: string},
      number
    >({
      query: (id) => ({
        url: `/townships/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        {type: "Township", id},
        {type: "Township", id: "LIST"},
      ],
    }),

    // Bulk Create Townships
    bulkCreateTownships: builder.mutation<
      {success: boolean; message: string; createdCount: number},
      {townships: CreateTownshipDto[]}
    >({
      query: (data) => ({
        url: "/townships/bulk",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Township"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateTownshipMutation,
  useGetTownshipsQuery,
  useGetTownshipStatisticsQuery,
  useGetTownshipsByRegionQuery,
  useGetTownshipBySlugQuery,
  useGetTownshipByIdQuery,
  useUpdateTownshipMutation,
  useDeleteTownshipMutation,
  useBulkCreateTownshipsMutation,
} = townshipApi;
